import { UuidSchema } from "@mind/contracts/browser";

export type FocusMode = "new" | "active";

export type FocusSearch = {
  readonly workspace?: string;
  readonly mode?: FocusMode;
  readonly thread?: string;
  readonly branch?: string;
  readonly history?: "open";
};

const FOCUS_SEARCH_KEYS: Readonly<Record<string, true>> = {
  workspace: true,
  mode: true,
  thread: true,
  branch: true,
  history: true,
};

export function validateFocusSearch(input: unknown): FocusSearch {
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    throw new TypeError("Focus route search must be an object");
  }

  const search = input as Readonly<Record<string, unknown>>;
  const unexpectedKey = Object.keys(search).find((key) => FOCUS_SEARCH_KEYS[key] !== true);
  if (unexpectedKey !== undefined) {
    throw new TypeError(`Unexpected Focus route search key: ${unexpectedKey}`);
  }

  let mode: FocusMode | undefined;
  if (search["mode"] !== undefined) {
    if (search["mode"] !== "new" && search["mode"] !== "active") {
      throw new TypeError("Invalid Focus mode");
    }
    mode = search["mode"];
  }
  const workspace = parseOptionalUuid(search["workspace"]);
  const thread = parseOptionalUuid(search["thread"]);
  const branch = parseOptionalUuid(search["branch"]);
  let history: "open" | undefined;
  if (search["history"] !== undefined) {
    if (search["history"] !== "open") throw new TypeError("Invalid Focus history state");
    history = search["history"];
  }

  if (mode === "active" && thread === undefined) {
    throw new TypeError("Active Focus mode requires a thread identifier");
  }
  if (mode === "new" && (thread !== undefined || branch !== undefined)) {
    throw new TypeError("New Focus mode cannot include thread identifiers");
  }
  if (branch !== undefined && thread === undefined) {
    throw new TypeError("A Focus branch requires a thread identifier");
  }

  return {
    ...(workspace === undefined ? {} : { workspace }),
    ...(mode === undefined ? {} : { mode }),
    ...(thread === undefined ? {} : { thread }),
    ...(branch === undefined ? {} : { branch }),
    ...(history === undefined ? {} : { history }),
  };
}


function parseOptionalUuid(value: unknown): string | undefined {
  return value === undefined ? undefined : UuidSchema.parse(value);
}

export function newFocusSearch(
  current: FocusSearch,
  history: FocusSearch["history"] | null = current.history ?? null,
): FocusSearch {
  return {
    ...(current.workspace === undefined ? {} : { workspace: current.workspace }),
    mode: "new",
    ...(history === null || history === undefined ? {} : { history }),
  };
}

export function activeFocusSearch(
  current: FocusSearch,
  thread: string,
  branch?: string,
  history: FocusSearch["history"] | null = current.history ?? null,
): FocusSearch {
  return validateFocusSearch({
    ...(current.workspace === undefined ? {} : { workspace: current.workspace }),
    mode: "active",
    thread,
    ...(branch === undefined ? {} : { branch }),
    ...(history === null || history === undefined ? {} : { history }),
  });
}

export function withHistoryState(current: FocusSearch, open: boolean): FocusSearch {
  return validateFocusSearch({ ...current, history: open ? "open" : undefined });
}
